import {
  deleteBundleComponent,
  listInventoryItems,
  postBundleComponent,
} from '@/lib/api/inventory.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FormEvent, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';

type BundleRow = {
  id?: unknown;
  componentItemId?: unknown;
  componentName?: unknown;
  quantity?: unknown;
};

type Props = {
  itemId: string;
  isBundle: boolean;
  bundleComponents: BundleRow[];
};

export function BundleSection({
  itemId,
  isBundle,
  bundleComponents,
}: Props): ReactElement {
  const queryClient = useQueryClient();
  const [bundleSearch, setBundleSearch] = useState('');
  const [bundleComponentId, setBundleComponentId] = useState('');
  const [bundleQty, setBundleQty] = useState('1');

  const bundleItemsQuery = useQuery({
    queryKey: ['inventory-items-bundle-pick', bundleSearch],
    queryFn: () => listInventoryItems({ search: bundleSearch, pageSize: 20, sellableOnly: true }),
    enabled: Boolean(itemId) && bundleSearch.trim().length >= 2,
  });

  const addBundleMutation = useMutation({
    mutationFn: (body: { componentItemId: string; quantity: number }) => {
      if (!itemId) throw new Error('Item id is required');
      return postBundleComponent(itemId, body);
    },
    onSuccess: () => {
      toastSuccess('Component added');
      setBundleSearch('');
      setBundleComponentId('');
      setBundleQty('1');
      void queryClient.invalidateQueries({ queryKey: ['inventory-item', itemId] });
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const removeBundleMutation = useMutation({
    mutationFn: (componentItemId: string) => {
      if (!itemId) throw new Error('Item id is required');
      return deleteBundleComponent(itemId, componentItemId);
    },
    onSuccess: () => {
      toastSuccess('Component removed');
      void queryClient.invalidateQueries({ queryKey: ['inventory-item', itemId] });
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  return (
    <section id="section-bundle" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-slate-950">Bundle</h2>
        {isBundle || bundleComponents.length > 0 ? (
          <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-800">
            Bundle
          </span>
        ) : null}
      </div>
      <p className="mb-3 text-sm text-slate-500">
        Add component parts to turn this item into a sellable bundle. Quantities are deducted from
        components when the bundle sells.
      </p>
      {bundleComponents.length === 0 ? (
        <p className="mb-3 text-sm text-slate-500">No components yet. Search and add parts below.</p>
      ) : (
        <ul className="mb-4 divide-y divide-slate-100 rounded-xl border border-slate-200">
          {bundleComponents.map((c) => (
            <li
              key={String(c.id)}
              className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
            >
              <div>
                <Link
                  to={`/inventory/${String(c.componentItemId)}`}
                  className="font-medium text-slate-900 hover:underline"
                >
                  {String(c.componentName)}
                </Link>
                <span className="ml-2 text-slate-500">× {String(c.quantity)}</span>
              </div>
              <button
                type="button"
                className="text-xs font-semibold text-red-600 hover:text-red-800"
                disabled={removeBundleMutation.isPending}
                onClick={() => removeBundleMutation.mutate(String(c.componentItemId))}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (!bundleComponentId) return;
          addBundleMutation.mutate({
            componentItemId: bundleComponentId,
            quantity: Math.max(0.01, Number(bundleQty) || 1),
          });
        }}
      >
        <label className="block min-w-[180px] flex-1">
          <span className="text-xs font-medium text-slate-600">Search component</span>
          <input
            className="mt-1 w-full rounded-xl border px-3 py-2 text-sm"
            value={bundleSearch}
            onChange={(e) => {
              setBundleSearch(e.target.value);
              setBundleComponentId('');
            }}
            placeholder="Type at least 2 characters…"
          />
        </label>
        <label className="block min-w-[200px] flex-1">
          <span className="text-xs font-medium text-slate-600">Component</span>
          <select
            className="mt-1 w-full rounded-xl border px-3 py-2 text-sm"
            value={bundleComponentId}
            onChange={(e) => setBundleComponentId(e.target.value)}
            required
          >
            <option value="">Select…</option>
            {(bundleItemsQuery.data?.data ?? [])
              .filter((i) => i.id !== itemId)
              .map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name}
                </option>
              ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Qty</span>
          <input
            type="number"
            min="0.01"
            step="0.01"
            className="mt-1 w-24 rounded-xl border px-3 py-2 text-sm"
            value={bundleQty}
            onChange={(e) => setBundleQty(e.target.value)}
          />
        </label>
        <button
          type="submit"
          disabled={addBundleMutation.isPending || !bundleComponentId}
          className="rounded-xl bg-slate-900 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          {addBundleMutation.isPending ? 'Adding…' : 'Add component'}
        </button>
      </form>
    </section>
  );
}
